'use client'

import { CloseButton } from '@/components/ui/CloseButton'
import { env } from '@/config/client'
import { usePointerDownOutside } from '@/hooks/browser/usePointerDownOutside'
import { cn } from '@/lib/utils'
import Link from 'next/link'
import {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'

type HamburgerNavMenuContextValue = {
  setOpened: Dispatch<SetStateAction<boolean>>
}

const HamburgerNavMenuContext = createContext<HamburgerNavMenuContextValue>({
  setOpened: () => {},
})

export type HamburgerNavMenuProps = PropsWithChildren<{
  classNames?: {
    container?: string
    popup?: string
    nav?: string
  }
  className?: string
}>

export function HamburgerNavMenu({ classNames, className, children }: HamburgerNavMenuProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const toggleButtonRef = useRef<HTMLButtonElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const navRef = useRef<HTMLElement>(null)
  const [opened, setOpened] = useState(false)
  usePointerDownOutside(containerRef, () => setOpened(false))
  return (
    <HamburgerNavMenuContext.Provider value={useMemo(() => ({ setOpened }), [])}>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        ref={containerRef}
        className={cn('relative', className, classNames?.container)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            toggleButtonRef?.current?.focus()
            setOpened(false)
          }
        }}
      >
        <button
          ref={toggleButtonRef}
          type="button"
          onClick={() => setOpened((prev) => !prev)}
          className={cn('rounded-md border-2 border-transparent p-1')}
        >
          <svg
            viewBox="0 0 24 24"
            className="h-5 w-5 fill-[#303D4B] md:h-6 md:w-6 lg:h-5 lg:w-5 dark:fill-[#C4C4C4]"
          >
            <path d="M3 18H21V16H3V18ZM3 13H21V11H3V13ZM3 6V8H21V6H3Z" />
          </svg>
        </button>
        <div
          className={cn(
            'absolute -left-1.5 -top-2 hidden rounded-lg bg-white bg-opacity-95 shadow-lg dark:bg-[#2B2B2B]',
            opened && 'block',
            classNames?.popup
          )}
        >
          <div className="flex flex-row items-center justify-between border-b border-[#B4C1CF] p-2">
            <CloseButton
              ref={closeButtonRef}
              onClick={() => {
                toggleButtonRef?.current?.focus()
                setOpened(false)
              }}
              onBlur={(event) => {
                if (event.relatedTarget === toggleButtonRef.current) {
                  setOpened(false)
                }
              }}
            />
            <p className="font-bold">{env.NEXT_PUBLIC_APP_VERSION}</p>
          </div>
          <nav
            ref={navRef}
            className={cn(
              'space-y-1 p-2 text-base font-bold text-[#0860C4] dark:text-[#1A94FF]',
              classNames?.nav
            )}
            onBlur={(event) => {
              if (
                event.relatedTarget !== closeButtonRef.current &&
                !navRef.current?.contains(event.relatedTarget)
              ) {
                event.preventDefault()
                toggleButtonRef?.current?.focus()
                setOpened(false)
              }
            }}
          >
            {children}
          </nav>
        </div>
      </div>
    </HamburgerNavMenuContext.Provider>
  )
}

export type HamburgerNavMenuItemProps = PropsWithChildren<{
  href: string
  className?: string
  onClick?: () => void
}>

export function HamburgerNavMenuItem({
  href,
  onClick,
  className,
  children,
}: HamburgerNavMenuItemProps) {
  const { setOpened } = useContext(HamburgerNavMenuContext)
  return (
    <Link
      href={href}
      onClick={() => {
        onClick?.()
        setOpened(false)
      }}
      className={cn(
        'flex flex-row gap-3 rounded p-3 outline-2 -outline-offset-1 outline-[#0860C4] dark:outline-[#0D4A80]',
        'hover:bg-[#ECF4FE] hover:outline dark:hover:bg-[#051E33]',
        'focus:bg-[#ECF4FE] focus:outline dark:focus:bg-[#051E33]',
        className
      )}
    >
      {children}
    </Link>
  )
}

export type HamburgerNavMenuButtonProps = PropsWithChildren<{
  onClick: () => void
  className?: string
}>

export function HamburgerNavMenuButton({
  onClick,
  className,
  children,
}: HamburgerNavMenuButtonProps) {
  const { setOpened } = useContext(HamburgerNavMenuContext)
  return (
    <button
      type="button"
      onClick={() => {
        onClick?.()
        setOpened(false)
      }}
      className={cn(
        'flex w-full flex-row gap-3 rounded p-3 outline-2 -outline-offset-1 outline-[#0860C4] dark:outline-[#0D4A80]',
        'hover:bg-[#ECF4FE] hover:outline dark:hover:bg-[#051E33]',
        'focus:bg-[#ECF4FE] focus:outline dark:focus:bg-[#051E33]',
        className
      )}
    >
      {children}
    </button>
  )
}
